import {
  collection,
  documentId,
  doc,
  getDocFromServer,
  getDocsFromServer,
  getCountFromServer,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import {
  InventoryAuthority,
  inventoryFirestorePath,
  InventoryItemSort,
  inventorySortOrder,
  createInventoryItemComparator,
} from '@qkindex/contracts';
import { db } from '../firebase';
import { Item } from '../domain/item';
import { Location } from '../domain/location';
import { DocumentLike, mapItemDocument, mapLocationDocument } from './mappers';

export const INVENTORY_PAGE_SIZE = 1000;
export interface InventoryPage<T> {
  records: T[];
  nextCursor?: string;
}

interface SortedCursor {
  version: 1;
  field: string;
  direction: string;
  primary: string | number;
  keywords: string;
  id: string;
  path: string;
}

/** Drain the last primary-key bucket before publishing: truncated text keys can
 * cover many records whose exact comparison order differs from document IDs. */
async function loadSortedItems(
  authority: InventoryAuthority,
  sort: InventoryItemSort,
  cursor?: string,
): Promise<InventoryPage<Item>> {
  const path = inventoryFirestorePath(authority, 'items');
  const reference = collection(db, path);
  const { field, direction } = inventorySortOrder(sort);
  const numeric =
    field !== 'inventorySort.keywords' && field !== 'inventorySort.location';
  let position: SortedCursor | undefined;
  if (cursor) {
    try {
      const parsed = JSON.parse(cursor) as SortedCursor;
      if (
        parsed.version !== 1 ||
        parsed.field !== field ||
        parsed.direction !== direction ||
        parsed.path !== path ||
        typeof parsed.id !== 'string' ||
        !parsed.id ||
        typeof parsed.keywords !== 'string' ||
        !(
          typeof parsed.primary === 'string' ||
          (typeof parsed.primary === 'number' &&
            Number.isFinite(parsed.primary))
        )
      )
        throw new Error();
      position = parsed;
    } catch {
      throw new Error('Invalid inventory sort cursor');
    }
  } else {
    // orderBy omits documents without its field. Never silently lose legacy data.
    const [all, covered] = await Promise.all([
      getCountFromServer(reference),
      getCountFromServer(
        query(reference, where('inventorySort.version', '==', 1)),
      ),
    ]);
    if (all.data().count !== covered.data().count) {
      const records: Item[] = [];
      let fallbackCursor: string | undefined;
      do {
        const page = await loadInventoryPage(
          authority,
          'items',
          fallbackCursor,
        );
        records.push(...page.records);
        fallbackCursor = page.nextCursor;
      } while (fallbackCursor);
      return { records: records.sort(createInventoryItemComparator(sort)) };
    }
  }
  const key = field.slice('inventorySort.'.length);
  const primary = (document: DocumentLike): string | number => {
    const value = (document.data().inventorySort as Record<string, unknown>)?.[
      key
    ];
    if (
      typeof value !== 'string' &&
      !(typeof value === 'number' && Number.isFinite(value))
    ) {
      throw new Error('Invalid inventory sort key');
    }
    return value as string | number;
  };
  const documents: DocumentLike[] = [];
  let boundary: string | number | undefined;
  let boundaryKeywords: string | undefined;
  let more = false;
  for (;;) {
    const snapshot = await getDocsFromServer(
      query(
        reference,
        orderBy(field, direction),
        ...(field !== 'inventorySort.keywords'
          ? [orderBy('inventorySort.keywords', 'asc')]
          : []),
        orderBy(documentId(), 'asc'),
        ...(position
          ? [
              startAfter(
                position.primary,
                ...(field !== 'inventorySort.keywords'
                  ? [position.keywords]
                  : []),
                position.id,
              ),
            ]
          : []),
        limit(INVENTORY_PAGE_SIZE + 1),
      ),
    );
    for (const document of snapshot.docs) {
      const value = primary(document);
      const keywords = (
        document.data().inventorySort as Record<string, unknown>
      ).keywords;
      if (typeof keywords !== 'string')
        throw new Error('Invalid inventory sort key');
      // Numeric and complete location keys can use the secondary keyword index.
      // A truncated location prefix must drain the whole primary bucket before
      // comparing full text, regardless of the secondary keyword index.
      const useSecondary =
        numeric ||
        (field === 'inventorySort.location' &&
          typeof boundary === 'string' &&
          boundary.length < 900);
      if (
        boundary !== undefined &&
        (value !== boundary || (useSecondary && keywords !== boundaryKeywords))
      ) {
        more = true;
        break;
      }
      documents.push(document);
      position = {
        version: 1,
        field,
        direction,
        primary: value,
        keywords,
        id: document.id,
        path,
      };
      if (documents.length === INVENTORY_PAGE_SIZE) {
        boundary = value;
        boundaryKeywords = keywords;
      }
    }
    if (more || snapshot.docs.length < INVENTORY_PAGE_SIZE + 1) break;
  }
  const records = documents
    .map(mapItemDocument)
    .filter((record): record is Item => !!record && !record.deleted);
  records.sort(createInventoryItemComparator(sort));
  return { records, ...(more ? { nextCursor: JSON.stringify(position) } : {}) };
}

// Document identity is a stable cursor, including for older records without edit timestamps.
export async function loadInventoryPage(
  authority: InventoryAuthority,
  kind: 'items',
  cursor?: string,
  sort?: InventoryItemSort,
): Promise<InventoryPage<Item>>;
export async function loadInventoryPage(
  authority: InventoryAuthority,
  kind: 'locations',
  cursor?: string,
  sort?: InventoryItemSort,
): Promise<InventoryPage<Location>>;
export async function loadInventoryPage(
  authority: InventoryAuthority,
  kind: 'items' | 'locations',
  cursor?: string,
  sort?: InventoryItemSort,
): Promise<InventoryPage<Item | Location>> {
  if (kind === 'items' && sort) return loadSortedItems(authority, sort, cursor);
  const snapshot = await getDocsFromServer(
    query(
      collection(db, inventoryFirestorePath(authority, kind)),
      orderBy(documentId()),
      ...(cursor ? [startAfter(cursor)] : []),
      limit(INVENTORY_PAGE_SIZE + 1),
    ),
  );
  const visible = snapshot.docs.slice(0, INVENTORY_PAGE_SIZE);
  const records: Array<Item | Location> = [];
  visible.forEach((document) => {
    const record =
      kind === 'items'
        ? mapItemDocument(document)
        : mapLocationDocument(document);
    if (record && !('deleted' in record && record.deleted))
      records.push(record);
  });
  return {
    records,
    ...(snapshot.docs.length > visible.length && visible.length
      ? { nextCursor: visible[visible.length - 1].id }
      : {}),
  };
}

/** Targeted post-mutation reads must never accept an older offline snapshot. */
export async function loadInventoryRecord(
  authority: InventoryAuthority,
  kind: 'items' | 'locations',
  id: string,
): Promise<Item | Location | undefined> {
  const snapshot = await getDocFromServer(
    doc(db, inventoryFirestorePath(authority, kind), id),
  );
  if (!snapshot.exists()) return undefined;
  const record =
    kind === 'items'
      ? mapItemDocument(snapshot)
      : mapLocationDocument(snapshot);
  return record && !('deleted' in record && record.deleted)
    ? record
    : undefined;
}
